
/**
 * Define a `withDefaults()` function, which interpolates default values into objects using special rules.
 *
 * A `withDefaults()` function will treat any object property whose value is
 * explicitly set to `undefined` as if the property does not exist. This means
 * that if the object has a property that is explicitly set to
 * `undefined`, then the value from defaults will be used for that property, and
 * if a property is `undefined` for `defaults` as well, then the
 * property will be entirely absent from the final object.
 *
 * The value `null` and all other falsy values are treated distinctly from
 * `undefined`. Presence of any falsy value other than `undefined` will be
 * handled in the same manner as `Object.assign()`.
 *
 * The output value is a new object. Neither of the input objects will be
 * modified.
 *
 * Note that this function differs from `Object.assign()` or object rest
 * expressions, or even Lodash's `_.defaults()` method, where explicitly-
 * assigned properties of later objects would override those of former objects
 * even where the value is explicitly set to `undefined`.
 *
 * @template D Type of the defaults object
 * @param defaults The object from which missing values will be substituted
 * @returns A new `withDefaults()` method
 *
 * @example
 * ```typescript
 * // Compare the behavior of `defineWithDefaults()` to alternatives:
 *
 * // - When the default is `undefined` and the object contains a defined value
 *                {...{foo: undefined}, ...{foo: 1}}; // --> { foo: 1 }
 *      Object.assign({foo: undefined},    {foo: 1}); // --> { foo: 1 }
 * defineWithDefaults({foo: undefined})(   {foo: 1}); // --> { foo: 1 }
 *
 * // - When default is defined and the object contains an `undefined`
 *                {...{foo: 1}, ...{foo: undefined}}; // --> { foo: undefined }
 *      Object.assign({foo: 1},    {foo: undefined}); // --> { foo: undefined }
 * defineWithDefaults({foo: 1})(   {foo: undefined}); // --> { foo: 1 }
 *
 * // - When `undefined` is passed explicitly for both objects
 *                {...{foo: undefined}, ...{foo: undefined}}; // --> { foo: undefined }
 *      Object.assign({foo: undefined},    {foo: undefined}); // --> { foo: undefined }
 * defineWithDefaults({foo: undefined})(   {foo: undefined}); // --> { }
 * ```
 */
export default function defineWithDefaults<D extends object>(defaults: Partial<D>) {
    const filteredDefaults = {} as D;
    for (const key in defaults) {
        if (defaults[key] === undefined) {
            continue;
        }
        filteredDefaults[key] = defaults[key];
    }

    return withDefaults;

    /**
     * A `withDefaults()` function that was created using `defineWithDefaults()`
     *
     * @template T Type of the input object
     * @param obj The object to have missing or undefined values interpolated into it
     * @return A new object composed from `defaults` and `obj`
     * @see defineWithDefaults
     */
    function withDefaults<T extends object>(obj: Partial<T>) {
        const filteredObj = {} as T;
        for (const key in obj) {
            if (obj[key] === undefined) {
                continue;
            }
            filteredObj[key] = obj[key];
        }

        return {...filteredDefaults, ...filteredObj};
    }
}
